import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "../components/ui/input-otp"
import { positiveInteger } from "./property-value-utils"

export function InputOtpRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()

  const inputChildren = (element.children ?? []).filter((c) => c.type === "input")
  const authoredLength = (element as unknown as { length?: unknown }).length
  const digitCount = inputChildren.length || positiveInteger(
    typeof authoredLength === "string" ? resolveSymbol(authoredLength) : authoredLength,
    6,
    12,
  )

  // Resolve bound OTP string
  const rawBound = element.bind
    ? String(resolveSymbol(element.bind.startsWith("#{") ? element.bind : `#{${element.bind}}`) ?? "")
    : undefined

  const midpoint = Math.floor(digitCount / 2)

  return (
    <InputOTP
      maxLength={digitCount}
      value={rawBound ?? ""}
      onChange={(value) => {
        // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
        dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value })
      }}
      className={cn(element.className)}
      data-testid={`input-otp-${element.id}`}
      aria-label={element.ariaLabel ?? "One-time password input"}
    >
      <InputOTPGroup>
        {Array.from({ length: midpoint }, (_, i) => (
          <InputOTPSlot
            key={i}
            index={i}
            aria-label={inputChildren[i]?.ariaLabel ?? `Digit ${i + 1}`}
          />
        ))}
      </InputOTPGroup>
      <InputOTPSeparator />
      <InputOTPGroup>
        {Array.from({ length: digitCount - midpoint }, (_, i) => (
          <InputOTPSlot
            key={midpoint + i}
            index={midpoint + i}
            aria-label={inputChildren[midpoint + i]?.ariaLabel ?? `Digit ${midpoint + i + 1}`}
          />
        ))}
      </InputOTPGroup>
    </InputOTP>
  )
}
